import { Random } from '../utils/Random.js';
import { playTone } from './proceduralSynth.js';

/**
 * Procedural Matrix-style underscore: minor pad, sub pulse, sparse digital arpeggio.
 * Optional looped file via CONFIG.audio.assets.backgroundMusic.
 */
export class MatrixBackgroundMusic {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {import('../config.js').CONFIG['audio']} config
   */
  constructor(ctx, bus, config) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config.music;
    this._rng = new Random(config.seed + 991);
    this._mix = 0;
    this._targetMix = 0;
    this._started = false;
    this._arpTimer = 0;
    this._arpIndex = 0;

    /** @type {GainNode | null} */
    this._master = null;
    /** @type {OscillatorNode[]} */
    this._oscillators = [];
    /** @type {AudioBufferSourceNode | null} */
    this._loopSource = null;

    const root = this._config.rootFrequency;
    this._arpNotes = (this._config.arpScale ?? [1, 1.2, 1.335, 1.5, 1.78]).map(
      (ratio) => root * ratio,
    );
    this._arpPattern = this._config.arpPattern ?? [0, 2, 4, 2, 1, 3, 0, 4, 2, 1];
  }

  /**
   * @param {() => AudioBuffer | null | undefined} getBuffer
   */
  start(getBuffer) {
    if (this._started || !this._config.enabled) return;
    this._started = true;

    this._master = this._ctx.createGain();
    this._master.gain.value = 0;
    this._master.connect(this._bus);

    const asset = getBuffer();
    if (asset) {
      this._startAssetLoop(asset);
    } else {
      this._buildPad();
      this._buildSubPulse();
    }

    this._arpTimer = this._rng.range(
      this._config.arpStepMin,
      this._config.arpStepMax,
    );
  }

  /**
   * @param {number} delta
   * @param {{ inSimulation?: boolean; menuOpen?: boolean }} state
   */
  update(delta, state) {
    if (!this._started || !this._master) return;

    const inSim = state.inSimulation ?? false;
    const menuOpen = state.menuOpen ?? false;
    const menuMix = this._config.menuMix ?? 0.55;
    const simMix = this._config.simulationMix ?? 1;

    if (inSim && !menuOpen) {
      this._targetMix = simMix;
    } else if (inSim && menuOpen) {
      this._targetMix = menuMix * 0.75;
    } else {
      this._targetMix = menuMix;
    }

    this._mix += (this._targetMix - this._mix) * Math.min(1, delta * 1.8);
    this._master.gain.value =
      this._mix * (this._config.masterGain ?? 1);

    if (this._loopSource) return;

    this._arpTimer -= delta;
    if (this._arpTimer > 0) return;
    this._arpTimer = this._rng.range(
      this._config.arpStepMin,
      this._config.arpStepMax,
    );
    if (this._rng.next() > (this._config.arpChance ?? 0.92)) return;
    this._playArpPluck();
  }

  dispose() {
    if (this._loopSource) {
      try {
        this._loopSource.stop();
      } catch {
        /* noop */
      }
      this._loopSource.disconnect();
      this._loopSource = null;
    }
    for (const osc of this._oscillators) {
      try {
        osc.stop();
      } catch {
        /* noop */
      }
      osc.disconnect();
    }
    this._oscillators = [];
    this._master?.disconnect();
    this._master = null;
    this._started = false;
  }

  /** @param {AudioBuffer} buffer */
  _startAssetLoop(buffer) {
    const source = this._ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = this._ctx.createGain();
    gain.gain.value = this._config.assetGain ?? 0.55;
    source.connect(gain);
    gain.connect(this._master);
    source.start();
    this._loopSource = source;
  }

  _buildPad() {
    const freqs = this._config.padFrequencies ?? [
      this._config.rootFrequency,
      this._config.rootFrequency * 1.2,
      this._config.rootFrequency * 1.5,
    ];

    const filter = this._ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = this._config.filterHz ?? 900;
    filter.Q.value = 0.35;
    filter.connect(this._master);

    const lfo = this._ctx.createOscillator();
    lfo.frequency.value = this._config.filterLfoHz ?? 0.04;
    const lfoGain = this._ctx.createGain();
    lfoGain.gain.value = this._config.filterLfoDepth ?? 180;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();
    this._oscillators.push(lfo);

    for (let i = 0; i < freqs.length; i += 1) {
      const osc = this._ctx.createOscillator();
      osc.type = i === 0 ? 'sine' : 'triangle';
      osc.frequency.value = freqs[i];
      const gain = this._ctx.createGain();
      gain.gain.value =
        (this._config.padGain ?? 0.06) * (i === 0 ? 1 : 0.55 / (i + 0.5));
      osc.connect(gain);
      gain.connect(filter);
      osc.start();
      this._oscillators.push(osc);
    }
  }

  _buildSubPulse() {
    const osc = this._ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = this._config.rootFrequency;

    const lfo = this._ctx.createOscillator();
    lfo.frequency.value = this._config.bassPulseHz ?? 0.42;
    const lfoGain = this._ctx.createGain();
    lfoGain.gain.value = this._config.bassGain ?? 0.08;
    lfo.connect(lfoGain);

    const amp = this._ctx.createGain();
    amp.gain.value = this._config.bassGain ?? 0.08;
    lfoGain.connect(amp.gain);

    const filter = this._ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 160;

    osc.connect(filter);
    filter.connect(amp);
    amp.connect(this._master);
    osc.start();
    lfo.start();
    this._oscillators.push(osc, lfo);
  }

  _playArpPluck() {
    const patternIdx = this._arpIndex % this._arpPattern.length;
    const noteIdx = this._arpPattern[patternIdx] % this._arpNotes.length;
    this._arpIndex += 1;

    let freq = this._arpNotes[noteIdx];
    if (this._rng.next() > 0.82) {
      freq *= 2;
    }

    const gain = (this._config.arpGain ?? 0.038) * this._rng.range(0.75, 1.15);
    playTone(this._ctx, this._master, {
      type: 'square',
      frequency: freq,
      detune: this._rng.range(-25, 25),
      duration: this._rng.range(0.12, 0.22),
      gain,
      attack: 0.008,
      decay: 0.14,
    });

    playTone(this._ctx, this._master, {
      type: 'sine',
      frequency: freq * 2.01,
      detune: this._rng.range(-15, 15),
      duration: 0.1,
      gain: gain * 0.35,
      attack: 0.004,
      decay: 0.08,
    });
  }
}
